// Phase 16-07 Task 1 — Quiet-hours editor for Settings → Notifications.
//
// Per 16-UI-SPEC.md §Component Inventory line 229 + §Visuals:
//   - Two <input type="time"> controls (start + end) with explicit
//     <label htmlFor> association (jest-axe accepts both label-for and
//     wrapping label; we use htmlFor here for test pinning of
//     label[for="qh-start"] / label[for="qh-end"]).
//   - A tz read-out paragraph with aria-live="polite" announcing the
//     IANA timezone (e.g. "Timezone: Europe/Stockholm"). Phase 16 boundary:
//     tz comes from the parent's value prop (which is itself sourced from
//     `Intl.DateTimeFormat().resolvedOptions().timeZone` at NotificationsCard
//     mount-time per CONTEXT decision). The aria-live="polite" matches the
//     UI-SPEC §Accessibility Contract line 245.
//   - Overnight-wrap hint: when value.start_hour > value.end_hour the
//     overnight hint paragraph is rendered (per CONTEXT decision — matches
//     backend `isInQuietHours` wrap-window semantics in
//     apps/backend/src/push/quiet-hours.ts).
//   - Touch-target minHeight 44px on every input (WCAG 2.5.5; UI-SPEC E2).
//   - Horizontal padding `0 12px` is legacy off-grid per UI-SPEC E1 (inherits
//     the sectionStyle 12px gap vocabulary; Phase 16 introduces ZERO new
//     off-grid values per E1 boundary commitment).
//   - fontSize 16px on the input prevents iOS Safari zoom-on-focus.
//
// Hour-granular contract (DEC: hour-granular UI per CONTEXT.md):
//   The <input type=time> reads/writes "HH:MM" strings. We floor minutes
//   to 0 and only round-trip the hour component. The backend `QuietHours`
//   schema uses integer hours 0-23 (Phase 7).

import type { JSX } from 'react'
import { FormattedMessage } from 'react-intl'

export interface QuietHoursValue {
  start_hour: number // 0-23
  end_hour: number // 0-23
  tz: string // IANA, autodetected by the caller (NotificationsCard)
}

export interface QuietHoursEditorProps {
  value: QuietHoursValue
  onChange: (next: QuietHoursValue) => void
}

const labelStyle: React.CSSProperties = {
  fontFamily: 'Fredoka, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  color: 'var(--color-text-primary)',
  marginBottom: '4px',
  display: 'block',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '44px', // WCAG 2.5.5 — UI-SPEC E2
  padding: '0 12px', // legacy off-grid per UI-SPEC E1 (inherit-only)
  border: '2px solid var(--color-surface-stroke)',
  borderRadius: '8px',
  background: 'var(--color-surface-mid)',
  color: 'var(--color-text-primary)',
  fontFamily: 'Manrope, system-ui, sans-serif',
  fontSize: '16px', // ≥16 prevents iOS Safari zoom-on-focus
  boxSizing: 'border-box',
}

function hourToHHMM(h: number): string {
  return `${String(h).padStart(2, '0')}:00`
}

function hhmmToHour(s: string): number {
  const firstSegment = s.split(':')[0] ?? '0'
  const h = Number.parseInt(firstSegment, 10)
  return Number.isFinite(h) && h >= 0 && h <= 23 ? h : 0
}

export function QuietHoursEditor({
  value,
  onChange,
}: QuietHoursEditorProps): JSX.Element {
  const startHHMM = hourToHHMM(value.start_hour)
  const endHHMM = hourToHHMM(value.end_hour)
  const isOvernight = value.start_hour > value.end_hour

  return (
    <fieldset
      aria-labelledby="quiet-hours-legend"
      data-component="quiet-hours-editor"
      style={{
        border: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <legend
        id="quiet-hours-legend"
        style={{
          fontFamily: 'Fredoka, sans-serif',
          fontWeight: 700,
          fontSize: '16px',
          color: 'var(--color-text-primary)',
          padding: 0,
          marginBottom: '4px',
        }}
      >
        <FormattedMessage
          id="settings.notifications.quiet_hours.label"
          defaultMessage="Quiet hours"
        />
      </legend>
      <p
        style={{
          margin: 0,
          fontFamily: 'Manrope, system-ui, sans-serif',
          fontWeight: 500,
          fontSize: '14px',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.5,
        }}
      >
        <FormattedMessage
          id="settings.notifications.quiet_hours.desc"
          defaultMessage="No pushes during these hours."
        />
      </p>

      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="qh-start" style={labelStyle}>
            <FormattedMessage
              id="settings.notifications.quiet_hours.start"
              defaultMessage="Start"
            />
          </label>
          <input
            id="qh-start"
            type="time"
            value={startHHMM}
            onChange={(e) =>
              onChange({
                ...value,
                start_hour: hhmmToHour(e.currentTarget.value),
              })
            }
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor="qh-end" style={labelStyle}>
            <FormattedMessage
              id="settings.notifications.quiet_hours.end"
              defaultMessage="End"
            />
          </label>
          <input
            id="qh-end"
            type="time"
            value={endHHMM}
            onChange={(e) =>
              onChange({
                ...value,
                end_hour: hhmmToHour(e.currentTarget.value),
              })
            }
            style={inputStyle}
          />
        </div>
      </div>

      <p
        aria-live="polite"
        style={{
          margin: 0,
          marginTop: '12px',
          fontFamily: 'Manrope, system-ui, sans-serif',
          fontWeight: 500,
          fontSize: '14px',
          color: 'var(--color-text-primary)',
          lineHeight: 1.5,
        }}
      >
        <FormattedMessage
          id="settings.notifications.quiet_hours.tz"
          defaultMessage="Timezone: {tz}"
          values={{ tz: value.tz }}
        />
      </p>

      {isOvernight && (
        <p
          style={{
            margin: 0,
            fontFamily: 'Manrope, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.5,
          }}
        >
          <FormattedMessage
            id="settings.notifications.quiet_hours.overnight_hint"
            defaultMessage="Overnight quiet hours active."
          />
        </p>
      )}
    </fieldset>
  )
}
